import React, { useEffect, useState } from "react";
import { Collapse, Checkbox, Button, Space, Flex } from "antd";
import { FilterFilled } from "@ant-design/icons";
import { ITableViewReport } from "../../../interfaces/table-view";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-solid-svg-icons";

interface Props {
  data: ITableViewReport[];
  onSubmit?: (updatedData: ITableViewReport[]) => void;
  isLoading?: boolean;
}

const SelectFieldsReport: React.FC<Props> = ({ data, onSubmit, isLoading = false }) => {
  const [jsonData, setJsonData] = useState<ITableViewReport[]>([]);
  const [enabledTables, setEnabledTables] = useState<string[]>([]);

  useEffect(() => {
    if (!Array.isArray(data)) return;

    setJsonData(data);

    const checkedTables = data
      .filter((tbl) => tbl.columns.some((col) => col.checked === 1))
      .map((tbl) => [tbl.tableName, ...(tbl.relations || [])])
      .flat();

    setEnabledTables(Array.from(new Set(checkedTables)));
  }, [data]);

  const handleCheckedChange = (tableIndex: number, columnIndex: number, value: boolean) => {
    const newData = [...jsonData];
    newData[tableIndex].columns[columnIndex].checked = value ? 1 : 0;
    setJsonData(newData);

    const currentTable = newData[tableIndex];
    const relatedTables = currentTable.relations || [];

    if (value) {
      const newEnabled = Array.from(new Set([...enabledTables, currentTable.tableName, ...relatedTables]));
      setEnabledTables(newEnabled);
    } else {
      const stillCheckedTables = newData
        .filter((tbl) => tbl.columns.some((col) => col.checked === 1))
        .map((tbl) => [tbl.tableName, ...(tbl.relations || [])])
        .flat();

      setEnabledTables(Array.from(new Set(stillCheckedTables)));
    }
  };

  const handleSubmit = () => {
    if (onSubmit) onSubmit(jsonData);
    else console.log("JSON result:", jsonData);
  };

  const handleClearAll = () => {
    const clearedData = jsonData.map((table) => ({
      ...table,
      columns: table.columns.map((col) => ({ ...col, checked: 0 })),
    }));

    setJsonData(clearedData);
    setEnabledTables([]);
  };

  const isCheckDisabled = (data: ITableViewReport[]) => {
    return data.some((table) => table.columns.some((col) => col.checked === 1));
  };

  const isTableEnabled = (tableName: string) => {
    return enabledTables.length === 0 || enabledTables.includes(tableName);
  };

  const collapseItems = jsonData.map((table, tableIndex) => {
    const disabledTable = !isTableEnabled(table.tableName);

    const checkedCount = table.columns.filter((col) => col.checked === 1).length;
    const totalCount = table.columns.length;

    return {
      key: table.tableName,
      label: (
        <Flex justify="space-between" align="center" style={{ width: "100%" }}>
          <b>{table.label}</b>
          <span style={{ fontWeight: 400, fontSize: 13 }}>
            {checkedCount}/{totalCount} Selected
          </span>
        </Flex>
      ),
      children: (
        <>
          {table.columns.map((col, colIndex) => (
            <Space
              key={col.column}
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                marginBottom: 8,
              }}
            >
              <Checkbox
                checked={!!col.checked}
                disabled={disabledTable}
                onChange={(e) => handleCheckedChange(tableIndex, colIndex, e.target.checked)}
              >
                {col.label}
              </Checkbox>

              {col.isFilter === 1 && <FilterFilled />}
            </Space>
          ))}
        </>
      ),
    };
  });

  return (
    <div className="wrap-select-field">
      <Flex align="center" justify="space-between">
        <div className="sub-title">Select Fields</div>
        <Space>
          <Button color="primary" variant="outlined" onClick={handleClearAll} disabled={!isCheckDisabled(jsonData)}>
            Clear all
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={isLoading} disabled={!isCheckDisabled(jsonData)}>
            <FontAwesomeIcon icon={faFile} style={{ fontSize: "16px" }} />
            Generate Report
          </Button>
        </Space>
      </Flex>
      <div className="sub">Please choose fields to create your customised report</div>
      <div className="content-fields">
        <Collapse items={collapseItems} defaultActiveKey={[collapseItems[0]?.key]} />
      </div>
    </div>
  );
};

export default SelectFieldsReport;
